import { Expression } from 'mapbox-gl'

import { IAPI } from 'src/api'
import { Provider, HomepageVehicle, Vehicle } from 'src/api/gen/api'
import { IMapSettings, MAP_LAYERS } from 'src/commons/map/types'
import { DEVICES_COLORS, DEVICES_ICONS } from 'src/components/Devices'

import { DockedStationEnum } from './LayersPopup'

export const MAP_VIEW_STORAGE = 'plato.supervision.satellite'
export const MAP_STOPS_LAYERS_STORAGE = 'plato.supervision.stopsLayers'
const MAP_IMAGE_SIZE = 50 // in pixels
export const DISPLAY_LOGO_BREAKPOINT = 17
export const SOURCE_NAME = 'assets'

export const fetchGBFS = async (api: IAPI) => {
  try {
    const response = await api.gbfs.gbfsDataList()
    return response
  } catch (err) {
    console.error('Cannot fetch gbfs', err)
    return {}
  }
}

type MapDevice = Vehicle | HomepageVehicle

export function devicesToGeoJSON(
  devices: MapDevice[],
  deviceToProperties: (d: any) => { [key: string]: any } = (d) => d
) {
  const features = devices
    .filter((device: MapDevice) => device.last_known_position && device.last_known_position.length)
    .map((device: MapDevice) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: device.last_known_position,
      },
      properties: deviceToProperties(device),
    }))

  return {
    type: 'FeatureCollection',
    features,
  }
}

export function addDevicesToMap(map: mapboxgl.Map, assets: MapDevice[], onLoaded?: () => void) {
  map.addSource(SOURCE_NAME, {
    type: 'geojson',
    data: devicesToGeoJSON(assets),
  } as any)

  map.addLayer({
    id: MAP_LAYERS.DeviceLowZoom,
    type: 'circle',
    maxzoom: DISPLAY_LOGO_BREAKPOINT,
    source: SOURCE_NAME,
    paint: {
      'circle-radius': {
        base: 2,
        stops: [
          [1, 1],
          [14, 6],
          [15, 7],
          [DISPLAY_LOGO_BREAKPOINT, 14],
        ],
      },
      'circle-color': [
        'match',
        ['get', 'type'],
        ...Array.prototype.concat(...Object.entries(DEVICES_COLORS)),
        '#ccc',
      ] as Expression,
    },
  })
  map.addLayer({
    id: MAP_LAYERS.DeviceHighZoom,
    source: SOURCE_NAME,
    type: 'symbol',
    minzoom: DISPLAY_LOGO_BREAKPOINT,
    layout: {
      'icon-image': [
        'match',
        ['get', 'type'],
        ...Array.prototype.concat(...Object.keys(DEVICES_ICONS).map((key) => [key, key])),
        'unspecified',
      ] as Expression,
      'icon-size': {
        base: 1,
        stops: [
          [15, 0.5],
          [24, 1],
        ],
      },
      'icon-allow-overlap': true,
      'icon-ignore-placement': false,
    },
  })

  // When all loading operations are done, the map enters an idle state
  if (onLoaded) {
    map.on('idle', onLoaded)
  }
}

export const getSatelliteFromStorage = () => localStorage.getItem(MAP_VIEW_STORAGE) as IMapSettings['satellite'] | null

export async function addMapImage(map: mapboxgl.Map, path: string, name: string): Promise<HTMLImageElement> {
  return new Promise((resolve) => {
    const img = new Image(MAP_IMAGE_SIZE, MAP_IMAGE_SIZE)
    img.src = path
    // Mapbox throws a SecurityException if we don't allow this, at least in development where the backoffice
    // stands on a different origin than the UI.
    // https://developer.mozilla.org/en-US/docs/Web/HTML/CORS_enabled_image
    img.crossOrigin = 'Anonymous'
    img.onload = () => {
      if (!map.hasImage(name)) {
        map.addImage(name, img)
      }
      resolve(img)
    }
  })
}

export function addImagesToMap(currentMap: mapboxgl.Map, activeProviders: Provider[]) {
  const promises = activeProviders
    .filter((provider: Provider) => provider.logo)
    .map((provider: Provider) => addMapImage(currentMap, provider.logo as string, provider.id))
  return Promise.all(promises)
}

export function clearMap(
  map: mapboxgl.Map,
  layers: string[] = [MAP_LAYERS.DeviceLowZoom, MAP_LAYERS.DeviceHighZoom],
  source: string = SOURCE_NAME
) {
  if (map.getSource(source)) {
    for (const layer of layers) {
      if (map.getLayer(layer)) {
        map.removeLayer(layer)
      }
    }
    map.removeSource(source)
  }
}

export interface IFakeMap {
  addLayer: (layer: string) => void
  getLayer: (id: string) => string | undefined
  getLayoutProperty: (layer: string, name: string) => string | undefined
  setLayoutProperty: (layer: string, name: string, value: string) => void
}

export const toggleGBFSLayersVisibility = (dockedStationLayers: DockedStationEnum[], map?: mapboxgl.Map | IFakeMap) => {
  if (!map) {
    return
  }
  const visibility = dockedStationLayers.find((value) => value == DockedStationEnum.citibike) ? 'visible' : 'none'
  if (map.getLayer('docked-bikes-layer')) {
    map.setLayoutProperty('docked-bikes-layer', 'visibility', visibility)
  }
  if (map.getLayer('docked-bikes-details')) {
    map.setLayoutProperty('docked-bikes-details', 'visibility', visibility)
  }
}
